import { useState } from 'react'
import _ from 'lodash'
import InfiniteScroll from 'react-infinite-scroll-component'
import { useErrorHandler } from 'react-error-boundary'
import { FormLoader, Toast, ProposalSection, Button } from '@vzmi/shp_shared_compo'
import { PROPOSAL_REVIEW_STATUS } from '@vzmi/shp_shared_compo/constants/proposal'
import { withCommonPageErrorBoundary } from '../../Common/HOC/withErrorBoundary'
import { withI13nWrapper } from '../../Common/I13nWrapper'
import LoadingSpinner, { SIZE } from '../../Common/LoadingSpinner/LoadingSpinner'
import withUpdateListingForm from '../../Proposal/Common/HOC/withUpdateListingForm'
import { FRONT_END_DEFINED_ERROR_CODE } from '../../../constants/globalTypes'
import { PROPOSAL_TYPE_NAME } from '../../../constants/proposalTypes'
import ProposalConsent from '../../Proposal/Common/ProposalConsent/ProposalConsent'
import ProposalCompletion from '../../Proposal/Common/ProposalCompletion/ProposalCompletion'
import { getFormValues } from '../../Proposal/Common/ProposalForm/Shared/util'
import ContentLayout from '../Common/ContentLayout'
import ActionHeader from '../Common/ActionHeader'
import ActionFooter from '../Common/ActionFooter'
import Background from '../Common/Background'
import BackToListingQueryLink from '../Common/BackToListingQueryLink'
import UpdateFailedListingsCard from '../Common/Card/UpdateFailedListings'
import UpdatedListingsTableCard from '../Common/Card/UpdatedListingsTable'
import NotFoundListingsSection from '../Common/Section/NotFoundListings'
import PreSubmitModalContent from '../Common/PreSubmitModalContent'
import UpdateFailedListingsSection from '../Common/Section/UpdateFailedListings'
import batchProposals from '../request/batchProposals'
import useBeforeunload from '../hook/useBeforeunload'
import usePageFlow from '../hook/usePageFlow'
import useI13N from '../hook/useI13N'
import useBackgroundFormDataOnInit from '../hook/useBackgroundFormDataOnInit'
import useListingsOnComposeInit from '../hook/useListingsOnComposeInit'
import useProposalsOnOverviewInit from '../hook/useProposalsOnOverviewInit'
import { useQueryString } from '@vzmi/shp_shared_compo/hook/useQueryString'
import useUIState from '../hook/useUIState'
import useListingState from '../hook/useListingState'
import { FlowContext } from '../helper/flow'
import {
  getBatchResult,
  getListingsWithProposal,
  getCombinedBatchResult,
  getDuplicatedListingsGroupByListingId
} from '../helper/batch'
import {
  getFormStateFromStorage,
  clearStateFromStorage
} from '../helper/storage'
import {
  DEFAULT_MENU_DATA,
  getParamsList,
  getListingsFieldErrors,
  getConvertedListings,
  getNonExistAttrMenuParams,
  getMenuResults,
  getCombinedMenuData,
  getCategoryOptionsMap,
  getUpdatedFormListingWithAttributes,
  getBatchApplyNewListings,
  getConvertedAttributeListing,
  getMappedErrorBatchResult
} from './helper'
import AttributeRows from './AttributeRows'
import styles from './index.css'

const DEFAULT_LISTINGS_FIELDS = [
  '-copy',
  '-images',
  '-videos',
  '-complimentaries',
  '-additionalPurchases',
  '-selectComplimentaries'
]

const ROW_SIZE = 5

const UpdateAttribute = props => {
  const {
    match: { params: { phase } },
    dispatch,
    change,
    initialize,
    actions,
    flags,
    user,
    proposalType,
    reduxFormState,
    i13nMethods,
    i13n
  } = props

  const [isAllowedLoadmore, setIsAllowedLoadmore] = useState(false)
  const { state: pageState, actions: pageActions } = useUIState(props)
  const { state: listingState, actions: listingActions } = useListingState()
  const {
    listings,
    deletedListings,
    queryStringListingIds,
    batchResult
  } = listingState
  const { successProposalMap, failedProposalMap } = batchResult
  const pageFlow = usePageFlow(phase, pageState.pageFlow)
  const {
    isEditPage,
    isConfirmPage,
    isSuccessPage,
    isOverviewPage,
    readOnly
  } = pageFlow

  const currentQueryStringListingIds = useQueryString(props, 'listingIds', true)

  // 在修改、確認、完成頁加上 GA 紀錄
  const i13nData = {
    proposalType,
    queryStringListingIds: currentQueryStringListingIds
  }
  useI13N(i13nMethods, i13nData, pageFlow)

  // 修改頁綁上離開頁面確認事件
  useBeforeunload(isOverviewPage)

  // 頁面初始化寫入賣場背景資料
  useBackgroundFormDataOnInit(props, initialize)

  const [
    duplicatedMap,
    setDuplicatedMap
  ] = useState({})
  const listingsParams = {
    id: currentQueryStringListingIds,
    fields: DEFAULT_LISTINGS_FIELDS
  }
  const handleError = useErrorHandler()
  const [visibleListings, setVisibleListings] = useState([])
  // 修改頁初始化取得批次賣場資料
  useListingsOnComposeInit(props, listingsParams, isEditPage,
    () => pageActions.setIsLoading(true),
    async (error, fetchedListings) => {
      if (error) {
        pageActions.setIsLoading(false)
        Toast.error(error.message)
        return
      }

      // 查無賣場
      if (_.isEmpty(fetchedListings)) {
        handleError({ code: FRONT_END_DEFINED_ERROR_CODE.FE_LISTING_NOT_FOUND })
        return
      }

      const savedFormListingMap = _.flow(
        () => _.get(getFormStateFromStorage(), 'listings', {}),
        listings => _.keyBy(listings, 'id')
      )()
      // 如果 okta 逾時重新取得 token 回來頁面會直接使用 localstorage 儲存的賣場修改狀態
      // 因為修改規格表有 loadmore 所以儲存的賣場狀態只有載入過的部分，沒有載入的則用預設賣場
      const currentListings = _.map(fetchedListings, listing => {
        const savedListing = _.get(savedFormListingMap, listing.id)
        if (savedListing) {
          return savedListing
        }
        return listing
      })
      const currentDuplicatedMap = getDuplicatedListingsGroupByListingId(currentListings)
      const convertedListings = getConvertedListings(currentListings)
      listingActions.setListings(convertedListings)
      listingActions.setQueryStringListingIds(currentQueryStringListingIds)
      setDuplicatedMap(currentDuplicatedMap)

      const currentVisibleListings = _.slice(convertedListings, 0, ROW_SIZE)
      const updatedMenuData = await updateMenuData(currentVisibleListings)
      updateCategoryOptions(currentVisibleListings, updatedMenuData)
      updateFormListingWithAttributes(convertedListings, updatedMenuData)
      pageActions.setIsLoading(false)
      setVisibleListings(currentVisibleListings)
      clearStateFromStorage()
    }
  )

  const currentQueryStringProposalIds = useQueryString(props, 'ids', true)
  const proposalsParams = {
    proposal: {
      id: currentQueryStringProposalIds
    },
    listing: {
      fields: DEFAULT_LISTINGS_FIELDS
    }
  }
  // 檢視頁初始化取得批次提案資料
  useProposalsOnOverviewInit(props, proposalsParams, isOverviewPage,
    () => pageActions.setIsLoading(true),
    async (error, currentProposals, currentListings) => {
      if (error) {
        pageActions.setIsLoading(false)
        Toast.error(error.message)
        return
      }

      // 查無提案
      if (_.isEmpty(currentProposals)) {
        handleError({ code: FRONT_END_DEFINED_ERROR_CODE.FE_PROPOSAL_NOT_FOUND })
        return
      }

      // 查無賣場
      if (_.isEmpty(currentListings)) {
        handleError({ code: FRONT_END_DEFINED_ERROR_CODE.FE_LISTING_NOT_FOUND })
        return
      }

      const currentBatchResult = getBatchResult(currentProposals.map(proposal => ([undefined, proposal])))
      const convertedListings = _.flow(
        () => getListingsWithProposal(currentListings, currentBatchResult),
        getConvertedListings
      )()
      pageActions.setIsLoading(false)
      listingActions.setListings(convertedListings)
      listingActions.setBatchResult(currentBatchResult)

      const currentVisibleListings = _.slice(convertedListings, 0, ROW_SIZE)
      const updatedMenuData = await updateMenuData(currentVisibleListings)
      updateCategoryOptions(currentVisibleListings, updatedMenuData)
      updateFormListingWithAttributes(convertedListings, updatedMenuData)
      pageActions.setIsLoading(false)
      setVisibleListings(currentVisibleListings)
    }
  )

  const [menuData, setMenuData] = useState(DEFAULT_MENU_DATA)
  const updateMenuData = async (currentListings) => {
    const nonExistAttrMenuParams = getNonExistAttrMenuParams(currentListings, menuData)
    const results = await getMenuResults(nonExistAttrMenuParams, actions)
    const newMenuData = getCombinedMenuData(menuData, results)
    setMenuData(newMenuData)
    return newMenuData
  }

  const [selectedCategoryOptionByListingId, setSelectedCategoryOptionByListingId] = useState({})
  const [categoryOptionsBySubStationId, setCategoryOptionsBySubStationId] = useState({})
  const updateCategoryOptions = (currentListings, currentMenuData, isUpdateSelectedOptions = true) => {
    // 既有的對應表
    const originMap = {
      // 按照賣場分類已選擇的規格表選單項目[父類項目、子類項目、規格項目]
      listing: selectedCategoryOptionByListingId,
      // 按照子站分類有選擇過的完整規格表選單
      subStation: categoryOptionsBySubStationId
    }
    const {
      listing: currentSelectedCategoryOptionByListingId,
      subStation: currentCategoryOptionsBySubStationId
    } = getCategoryOptionsMap(currentListings, currentMenuData, originMap)
    // 更新選單父類、子類選項時不需要把賣場真正選擇的選項更新掉
    // 因為選單有三層，可能選完前兩層就離開選單，需要幫他還原
    if (isUpdateSelectedOptions) {
      setSelectedCategoryOptionByListingId(currentSelectedCategoryOptionByListingId)
    }
    setCategoryOptionsBySubStationId(currentCategoryOptionsBySubStationId)
    return [
      currentSelectedCategoryOptionByListingId,
      currentCategoryOptionsBySubStationId
    ]
  }

  const updateFormListingWithAttributes = (currentListings, currentMenuData) => {
    const currentListingMap = _.keyBy(currentListings, 'id')
    const convertedListings = _.map(visibleListings, listing => _.get(currentListingMap, listing.id, listing))
    const convertedListingMap = _.keyBy(convertedListings, 'id')
    // 不在從顯示中賣場代表是新加入的賣場 EX: loadmore
    const newListings = _.filter(currentListings, currentListing => !(currentListing.id in convertedListingMap))
    const updateableListings = [
      // 既有賣場要重置規格項目
      ..._.filter(currentListings, currentListing => currentListing.isReplaceAttributes),
      // 新賣場要更新預設規格項目
      ...newListings
    ]
    const totalListings = [
      ...convertedListings,
      ...newListings
    ]
    const updatedListingById = _.flow(
      // 將全部賣場的規格項目含有預設值的填入值
      () => getUpdatedFormListingWithAttributes(updateableListings, currentMenuData),
      newConvertedListings => {
        const newConvertedListingMapById = _.keyBy(newConvertedListings, 'id')
        return _.map(totalListings, listing => {
          // 有更新預設規格項目的賣場拿新的 (loadmore 新增的賣場 / 規格表遺失重選的賣場)
          // 沒有更新的拿既有的繼續用 (既有顯示中且沒有指定覆蓋既有規格項目(isReplaceAttributes)的賣場)
          return _.get(newConvertedListingMapById, listing.id, listing)
        })
      },
      // 以賣場編號做對應表
      updatedFormListingAttributes => _.keyBy(updatedFormListingAttributes, 'id')
    )()

    const formListings = _.flow(
      _.cloneDeep,
      originListings => _.map(originListings, originListing => {
        return _.get(updatedListingById, originListing.id, originListing)
      })
    )(totalListings)
    changeFormListings(formListings)
  }

  // 規格表選單選擇未選擇過的項目會需要發出請求，請求過程中該選項會維持 loading 狀態從這裡控制
  const triggerCategoryOptionLoadingState = (listing, catOption, catItemOption) => {
    const { subStationId } = listing
    const currentCategoryOptions = _.cloneDeep(
      _.get(categoryOptionsBySubStationId, subStationId, [])
    )
    const selectCatIndex = _.get(catOption, 'index')
    if (_.isUndefined(catItemOption)) {
      _.set(currentCategoryOptions, `${selectCatIndex}.loading`, true)
      setCategoryOptionsBySubStationId({
        ...categoryOptionsBySubStationId,
        [subStationId]: currentCategoryOptions
      })
      return
    }

    const catItemIndex = _.get(catItemOption, 'index')
    _.set(currentCategoryOptions, `${selectCatIndex}.children[${catItemIndex}].loading`, true)
    setCategoryOptionsBySubStationId({
      ...categoryOptionsBySubStationId,
      [subStationId]: currentCategoryOptions
    })
  }

  // 修改 redux form 裡的賣場
  const changeFormListings = listings => change('listings', listings)

  // 完成提案
  const [listingsFieldErrors, setListingsFieldErrors] = useState({})
  const onComplete = async () => {
    const formValues = getFormValues(reduxFormState)
    const formListings = _.get(formValues, 'listings', [])
    const formListingMapById = _.keyBy(formListings, 'id')
    const paramsList = getParamsList(
      formValues,
      PROPOSAL_REVIEW_STATUS.DRAFT,
      duplicatedMap
    )
    const proposals = await batchProposals(actions, paramsList)
    const originalBatchResult = getBatchResult(proposals)
    const mappedErrorBatchResult = getMappedErrorBatchResult(originalBatchResult)
    const combinedBatchResult = getCombinedBatchResult(
      batchResult,
      mappedErrorBatchResult
    )
    const {
      successProposalMap,
      failedProposalMap
    } = combinedBatchResult
    // 如果送出的規格表修改在後端驗證失敗
    // 這裡會把錯誤訊息對照 redux form 格式組出對應欄位的錯誤訊息
    const currentListingsFieldErrors = getListingsFieldErrors(formListings, failedProposalMap)
    const covertedListings = _.flow(
      // 完成提案後用含有提案編號的資料取代原本的賣場資料
      // 送出提案、返回修改再次完成提案時才有 proposalId 可以使用
      () => getListingsWithProposal(formListings, combinedBatchResult),
      // 將送出前"同時修改商品保證"狀態保留下來
      listingsWithProposal => _.map(listingsWithProposal, (listing) => {
        const originListing = _.get(formListingMapById, listing.id, {})
        return {
          ...listing,
          // 送出提案後表單資料會被回應 proposal 裡的 listing 取代
          // 為了 redux form 使用的表單資料會消失，因此需要從 redux form 裡把這些資料先拉回來再更新表單
          ..._.pick(originListing, [
            'struDataAttrClusterId',
            'convertedAttributeMapByClusterId',
            'isInConsistentProduct',
            'attributes',
            'hasSpecLevel',
            'layerOneModel',
            'layerTwoModel'
          ])
        }
      }),
      // 從 api 取得的賣場資料要再轉換成前端格式給 component 使用
      getConvertedListings
    )()
    const updatedListings = _.filter(covertedListings, listing => {
      return (listing.id in successProposalMap)
    })
    listingActions.setListings(covertedListings)
    listingActions.setBatchResult(combinedBatchResult)
    changeFormListings(covertedListings)
    setListingsFieldErrors(currentListingsFieldErrors)
    return [null, updatedListings, currentListingsFieldErrors]
  }

  // 送出提案
  const onPendingReview = async () => {
    const formValues = getFormValues(reduxFormState)
    const {
      failedProposalMap: onCompleteFailedProposalMap
    } = batchResult
    const paramsList = _.filter(
      getParamsList(
        formValues,
        PROPOSAL_REVIEW_STATUS.PENDING_REVIEW,
        duplicatedMap
      ),
      // 送出提案過濾掉完成提案時失敗的提案
      params => !(_.get(params, 'listing.id') in onCompleteFailedProposalMap)
    )
    const proposals = await batchProposals(actions, paramsList)
    const combinedBatchResult = getCombinedBatchResult(
      batchResult,
      getBatchResult(proposals)
    )
    const {
      successProposalMap,
      failedProposalMap
    } = combinedBatchResult
    const currentListingsFieldErrors = getListingsFieldErrors(listings, failedProposalMap)
    const covertedListings = _.flow(
      () => getListingsWithProposal(listings, combinedBatchResult),
      getConvertedListings
    )()
    const updatedListings = _.filter(covertedListings, listing => {
      return (listing.id in successProposalMap)
    })
    listingActions.setListings(covertedListings)
    listingActions.setBatchResult(combinedBatchResult)
    changeFormListings(covertedListings)
    setListingsFieldErrors(currentListingsFieldErrors)
    return [null, updatedListings, currentListingsFieldErrors]
  }

  // 下次再改按鈕觸發刪除該賣場於 state 跟 redux form 的資料
  // 並重新計算可能含有共用商品的賣場
  const onRemoveListing = async (id, listings) => {
    const newListings = []
    const deletedListings = []
    for (const listing of listings) {
      if (listing.id === id) {
        deletedListings.push(listing)
      } else {
        newListings.push(listing)
      }
    }
    const currentDuplicatedMap = getDuplicatedListingsGroupByListingId(newListings)
    // state 紀錄被刪除的賣場、更新既有賣場
    listingActions.addDeletedListings(deletedListings)
    // redux form 更新賣場欄位
    changeFormListings(newListings)
    // state 重新計算含有共用商品的賣場
    setDuplicatedMap(currentDuplicatedMap)
  }

  const [totalRowsSize, visibleRowsSize] = _.map([listings, visibleListings], _.size)
  // 載入更多時只會從這些賣場裡找出"未發出請求"或是"請求失敗"的規格表資料
  const loadmore = async () => {
    const formListingMap = _.flow(
      // 目前 redux form 表單資料
      () => _.get(reduxFormState, 'values.listings', []),
      // 轉換成 listing id 對應表
      listings => _.keyBy(listings, 'id')
    )()
    const nextVisibleListings = _.flow(
      // 從全部賣場中切出加入 loadmore 後要顯示的賣場
      () => _.slice(listings, 0, visibleRowsSize + ROW_SIZE),
      visibleListings => _.map(visibleListings, visibleListing => {
        const formListing = _.get(formListingMap, visibleListing.id)
        // 不在 redux form 的賣場代表是首次顯示，直接用預設值寫入 redux form
        if (_.isEmpty(formListing)) {
          return visibleListing
        }
        // 已經在 redux form 的賣場直接沿用表單資料，避免修改過的資料被蓋回預設值
        return formListing
      })
    )()
    const updatedMenuData = await updateMenuData(nextVisibleListings)
    updateCategoryOptions(nextVisibleListings, updatedMenuData)
    // 從回應取得的資料、或是既有已存在的資料更新 redux form
    updateFormListingWithAttributes(nextVisibleListings, updatedMenuData)
    setVisibleListings(nextVisibleListings)
  }
  const changeReduxFromListings = async (listing) => {
    pageActions.setIsLoading(true)
    const formListingMap = _.flow(
      // 目前 redux form 表單資料
      () => _.get(reduxFormState, 'values.listings', []),
      // 轉換成 listing id 對應表
      listings => _.keyBy(listings, 'id')
    )()
    const newListings = _.flow(
      // 從全部賣場中切出加入 loadmore 後要顯示的賣場
      () => _.map(listings, visibleListing => {
        const formListing = _.get(formListingMap, visibleListing.id)
        // 不在 redux form 的賣場代表是首次顯示，直接用預設值寫入 redux form
        if (_.isEmpty(formListing)) {
          return visibleListing
        }
        // 已經在 redux form 的賣場直接沿用表單資料，避免修改過的資料被蓋回預設值
        return formListing
      }),
      listings => _.map(listings, getConvertedAttributeListing)
    )()
    const updatedMenuData = await updateMenuData(listings)
    updateCategoryOptions(listings, updatedMenuData)

    // 將所有賣場attributes轉換成批次套用資料attributes
    const convertedBatchData = _.flow(
      () => getBatchApplyNewListings(
        listing,
        getUpdatedFormListingWithAttributes(newListings, updatedMenuData)
      ),
      listings => _.map(listings, getConvertedAttributeListing)
    )()

    // 重組attributes回listings格式(EX.{name:xx,values:xx})
    const updatedListings = _.map(convertedBatchData, listing => {
      return {
        ...listing,
        attributes: _.map(listing.attributes, attribute => {
          return { name: attribute.name, values: attribute.selectedValues }
        })
      }
    })
    listingActions.setListings(updatedListings)
    setVisibleListings(_.slice(updatedListings, 0, visibleRowsSize))
    // 修改 redux form 裡的賣場
    changeFormListings(convertedBatchData)
    pageActions.setIsLoading(false)
  }

  // header footer context
  // 避免在主要表單中使用此 context 避免重複 render component
  const flowContextValue = {
    phase,
    pageFlow: pageState.pageFlow,
    isLoading: pageState.isLoading,
    isPending: pageState.isPending,
    setPageFlow: pageActions.setPageFlow,
    setIsLoading: pageActions.setIsLoading,
    setIsPending: pageActions.setIsPending
  }
  const isMoreRowsExist = (visibleRowsSize < totalRowsSize)
  const proposalTypeName = `批次${PROPOSAL_TYPE_NAME[proposalType]}`
  const completeName = `${proposalTypeName}已送出`

  if (_.isEmpty(listings)) {
    return (
      <FormLoader
        isLoading
        fixed
      />
    )
  }

  return (
    <ContentLayout isSuccessPage={isSuccessPage}>
      <FormLoader
        isLoading={pageState.isLoading}
        fixed
      >
        {
          // 完成頁
          isSuccessPage && (
            <>
              <ProposalCompletion
                title={completeName}
                proposalType={proposalType}
                withHint={false}
              />
              <UpdateFailedListingsCard
                proposalTypeName={proposalTypeName}
                listings={listings}
                failedMap={failedProposalMap}
              />
              <UpdatedListingsTableCard
                proposalType={proposalType}
                listings={listings}
                successMap={successProposalMap}
              />
            </>
          )
        }
        {
          // 修改、確認、檢視頁
          !isSuccessPage && (
            <>
              <FlowContext.Provider value={flowContextValue}>
                <ActionHeader
                  title={proposalTypeName}
                  listingIds={queryStringListingIds}
                  fieldErrors={listingsFieldErrors}
                />
              </FlowContext.Provider>
              <Background
                flags={flags}
                user={user}
                isEditPage={isEditPage}
                readOnly={readOnly}
              />
              {isConfirmPage && <ProposalConsent />}
              <NotFoundListingsSection
                listingIdsFromUrl={queryStringListingIds}
                listings={listings}
                deletedListings={deletedListings}
                isVisible={isEditPage}
              />
              <UpdateFailedListingsSection
                proposalTypeName={proposalTypeName}
                listings={listings}
                failedMap={failedProposalMap}
              />
              <ProposalSection
                title='賣場規格表'
                data-role='batch-update-copy'
              >
                <InfiniteScroll
                  dataLength={visibleRowsSize}
                  next={loadmore}
                  hasMore={isMoreRowsExist && isAllowedLoadmore}
                  loader={<LoadingSpinner size={SIZE.MEDIUM} />}
                  style={{ overflow: 'hidden' }}
                  scrollThreshold={0.9}
                >
                  <AttributeRows
                    readOnly={readOnly}
                    rowsSize={visibleRowsSize}
                    onRemove={onRemoveListing}
                    batchResult={batchResult}
                    duplicatedMap={duplicatedMap}
                    isOverviewPage={isOverviewPage}
                    change={change}
                    proposalType={proposalType}
                    menuData={menuData}
                    selectedOptionMap={selectedCategoryOptionByListingId}
                    optionsMap={categoryOptionsBySubStationId}
                    updateMenuData={updateMenuData}
                    updateCategoryOptions={updateCategoryOptions}
                    updateFormListingWithAttributes={updateFormListingWithAttributes}
                    triggerOptionLoadingState={triggerCategoryOptionLoadingState}
                    i13n={i13n}
                    changeReduxFromListings={changeReduxFromListings}
                  />
                </InfiniteScroll>
                {(isMoreRowsExist && !isAllowedLoadmore) && (
                  <div className={styles.allowLoadmoreBtnArea}>
                    <Button
                      onClick={() => setIsAllowedLoadmore(true)}
                      size='large'
                      buttonType='outline'
                      fluid
                    >
                      載入更多
                    </Button>
                  </div>
                )}
              </ProposalSection>
              <FlowContext.Provider value={flowContextValue}>
                <ActionFooter
                  dispatch={dispatch}
                  batchResult={batchResult}
                  onComplete={onComplete}
                  onPendingReview={onPendingReview}
                  preSubmitContent={(
                    <PreSubmitModalContent
                      duplicatedMap={duplicatedMap}
                    />
                  )}
                  withPreSubmit={!_.isEmpty(duplicatedMap)}
                />
              </FlowContext.Provider>
            </>
          )
        }
      </FormLoader>
    </ContentLayout>
  )
}

const hoc = _.flow([
  withI13nWrapper,
  // common page: yahoo title & supplier part
  // error boundary
  withCommonPageErrorBoundary(<BackToListingQueryLink />),
  // proposal redux form init
  // forceUnregisterOnUnmount 避免從完成提案返回時 sync error 被清除
  Component => withUpdateListingForm(Component, {
    forceUnregisterOnUnmount: false,
    // 每次初始化表單自動觸發重新驗證
    // FieldArray 在重新選擇規格表時不會重新驗證表單內容
    // 這會導致預設錯誤訊息提示保持在舊的規格項目
    shouldError: () => true
  })
])

export default hoc(UpdateAttribute)
